import { useTranslation } from "react-i18next";
import classNames from "classnames";
import useSWR from "swr";
import { Tooltip } from "reactstrap";
import { faUtensils } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import WaiveModal from "./WaiveModal";
import useApi from "../../../../../../../utils/api";
import { useAccess } from "../../../../../../../utils/hooks/access";
import { useVisible } from "../../../../../../../utils/hooks/useVisible";

const WaiveTooltip = ({ data, waive, cancelWaive, isMyTimesheet, timesheets }) => {
    const { t } = useTranslation();
    const { visible: tooltipVisible, toggle: toggleTooltip } = useVisible();
    const id = `meal-waive-${data.user.id}-row-${data.id}`;
    const {
        visible: confirmationModalVisible,
        open: openConfirmationModal,
        close: closeConfirmationModal,
    } = useVisible();

    const { authGet } = useApi();
    const {
        data: mealPremiumRule,
        isLoading: isLoadingMealPremiumRule,
    } = useSWR(
        () => {
            if (!data?.calculationGroup?.mealPremium?.id) {
                return null;
            }
            return `/calc-rules/meal-premium/${data.calculationGroup.mealPremium.id}`;
        },
        (url) => {
            return authGet(url);
        },
    );

    const mealPremiums = timesheets.filter((item) => {
        return item.appliedRules?.includes("mealPremium");
    });
    const waivedPremium = mealPremiums.find((item) => {
        return !!item.mealWaiveStatus;
    });
    const isManagerWaive = !!waivedPremium?.appliedRules?.includes("mealPremium--managerWaive");

    const { hasAccess: canCreateMealWaive } = useAccess("requests.timesheet.mealPremiumWaive.canCreate");
    const { hasAccess: canApproveMealWaive } = useAccess("requests.timesheet.mealPremiumWaive.team.canApprove");
    const canWaive = (
        (
            isMyTimesheet
            && mealPremiumRule?.canWaive
            && !waivedPremium
            && canCreateMealWaive
        )
        || (
            !isMyTimesheet
            && mealPremiumRule?.canManagerWaive
            && (waivedPremium?.appliedRules?.includes("mealPremium--managerWaive") || !waivedPremium)
            && canApproveMealWaive
        )
    );

    if (!data.mealWaiveStatus && !canWaive) {
        return null;
    }

    // TODO: depending on the user who waived the premium, the approved tooltip should show either that the request was approved or that the manager waived the premium itself

    return (
        <span id={id}>
            <FontAwesomeIcon
                className={classNames(
                    (!waivedPremium && isMyTimesheet) && "text-primary",
                    waivedPremium?.mealWaiveStatus === "approved" && "text-info",
                    "cursor-pointer"
                )}
                icon={faUtensils}
                size="lg"
                onClick={
                    canWaive
                        ? () => openConfirmationModal()
                        : undefined
                }
            />

            <Tooltip target={id} isOpen={tooltipVisible} toggle={toggleTooltip}>
                <span className="text-sm font-weight-bolder px-2 m-2">
                    {!waivedPremium?.mealWaiveStatus && canWaive && t("click-to-waive-meal-premium")}
                    {!waivedPremium?.mealWaiveStatus && !canWaive && t("meal-premium-not-available-for-waive")}
                    {waivedPremium?.mealWaiveStatus === "pending" && t("meal-waive-request-info")}
                    {waivedPremium?.mealWaiveStatus === "approved" && t("meal-waive-request-approved")}
                </span>
            </Tooltip>

            <WaiveModal
                isOpen={confirmationModalVisible}
                onClose={closeConfirmationModal}
                onConfirm={(values) => isManagerWaive
                    ? cancelWaive(data.date, values)
                    : waive(data.date, values)
                }
                canWaive={canWaive}
                mealPremiumRule={mealPremiumRule}
                isLoadingMealPremiumRule={isLoadingMealPremiumRule}
                isMyTimesheet={isMyTimesheet}
                mealPremiums={mealPremiums}
                isManagerWaive={isManagerWaive}
            />
        </span>
    )
}

export default WaiveTooltip;
